import { Link } from 'react-router-dom'
import { cn } from '@/lib/tailwind.utils'
import { useTocScrollSpy } from '../hooks/useTocScrollSpy'

export type TocItem = {
  id: string
  title: string
  children: Array<{ id: string; title: string }>
}

type Props = {
  tableOfContents?: TocItem[]
  className?: string
}

export function GuideTableOfContents({ tableOfContents = [], className }: Props) {
  const { toc, isActive, onTocClick } = useTocScrollSpy(tableOfContents, { topOffset: 100 })

  if (toc.length === 0) return null

  return (
    <div
      className={cn(
        'hidden xl:block xl:sticky xl:top-[4.75rem] xl:-mr-6 xl:h-[calc(100vh-4.75rem)] xl:flex-none xl:overflow-y-auto xl:pt-16 xl:pb-16 xl:pr-6',
        className,
      )}
    >
      <nav aria-labelledby="on-this-page-title" className="w-60">
        <h2 id="on-this-page-title" className="ml-4 text-sm font-medium leading-5 text-slate-900">
          In this article
        </h2>

        <div className="ml-4 mt-4 border-l-4 border-slate-200 pl-4">
          <ol role="list" className="list-none p-0 text-sm leading-5">
            {toc.map((section) => (
              <li key={section.id}>
                <h3 className="m-0 [font:inherit]">
                  <Link
                    to={`#${section.id}`}
                    onClick={(e) => onTocClick(e, section.id)}
                    aria-current={isActive(section) ? 'true' : undefined}
                    className={cn(
                      'relative flex h-9 items-center pl-3 pr-1 no-underline transition-colors duration-150',
                      isActive(section)
                        ? "font-semibold text-slate-900 before:absolute before:-left-5 before:top-1 before:bottom-1 before:w-1 before:rounded-sm before:bg-sky-500 before:content-['']"
                        : 'font-normal text-slate-500 hover:text-slate-700',
                    )}
                  >
                    <span className="block min-w-0 truncate">{section.title}</span>
                  </Link>
                </h3>

                {section.children.length > 0 && (
                  <ol role="list" className="mt-2 list-none space-y-3 pl-3">
                    {section.children.map((sub) => (
                      <li key={sub.id}>
                        <Link
                          to={`#${sub.id}`}
                          onClick={(e) => onTocClick(e, sub.id)}
                          aria-current={isActive(sub) ? 'true' : undefined}
                          className={cn(
                            'relative flex h-8 items-center pl-3 pr-1 text-slate-500 no-underline transition-colors duration-150',
                            isActive(sub)
                              ? "font-semibold text-slate-900 before:absolute before:-left-8 before:top-1 before:bottom-1 before:w-1 before:rounded-sm before:bg-sky-500 before:content-['']"
                              : 'hover:text-slate-600',
                          )}
                        >
                          <span className="block min-w-0 truncate">{sub.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                )}
              </li>
            ))}
          </ol>
        </div>
      </nav>
    </div>
  )
}
